/**
 * Particle-style fixed-slot pool for sonar line streaks.
 * Spawn/kill without allocating; overwrite oldest when full (like a particle emitter).
 */
import type { SonarBeam, SonarHitKind } from "./sonar-fx";
import {
  beamColor,
  contactSurfaceColor,
  memoryPaintFromRail,
  objectInteriorColor,
  reflectRailColor,
} from "./sonar-fx";

/** Minimal Graphics surface used by the pool (Phaser Graphics satisfies this). */
export type LineDrawTarget = {
  clear(): void;
  lineStyle(width: number, color: number, alpha?: number): void;
  lineBetween(x1: number, y1: number, x2: number, y2: number): void;
  fillStyle(color: number, alpha?: number): void;
  fillTriangle(
    x0: number,
    y0: number,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
  ): void;
  fillCircle(x: number, y: number, radius: number): void;
};

export type LineParticle = {
  alive: boolean;
  ox: number;
  oy: number;
  hx: number;
  hy: number;
  rx: number;
  ry: number;
  nx: number;
  ny: number;
  amp: number;
  kind: SonarHitKind;
  hit: boolean;
  color: number;
  age: number;
  life: number;
  contacted: boolean;
  /** How far outbound memory/fog has already been stamped (0..1). */
  memOut: number;
  /** How far reflect memory has been stamped (0..1). */
  memRef: number;
  /** Monotonic spawn order for oldest-overwrite. */
  stamp: number;
};

export type ContactParticle = {
  alive: boolean;
  x: number;
  y: number;
  nx: number;
  ny: number;
  kind: SonarHitKind;
  amp: number;
  life: number;
  maxLife: number;
  peak: number;
  stamp: number;
};

export type SonarLinePoolOptions = {
  beamCapacity: number;
  contactCapacity: number;
  /** Tip travel multiplier; life burn stays on sonarAgeMul. Default 1. */
  tipSpeedMul?: number;
};

/** How to pick a slot when the beam pool is full. */
export type BeamAcquireMode = "overwriteOldest" | "preferSpent";

function makeBeamSlot(): LineParticle {
  return {
    alive: false,
    ox: 0,
    oy: 0,
    hx: 0,
    hy: 0,
    rx: 0,
    ry: 0,
    nx: 0,
    ny: 0,
    amp: 0,
    kind: "void",
    hit: false,
    color: 0,
    age: 0,
    life: 1,
    contacted: false,
    memOut: 0,
    memRef: 0,
    stamp: 0,
  };
}

function makeContactSlot(): ContactParticle {
  return {
    alive: false,
    x: 0,
    y: 0,
    nx: 0,
    ny: 0,
    kind: "wall",
    amp: 0,
    life: 0,
    maxLife: 1,
    peak: 0,
    stamp: 0,
  };
}

/** Baseline fraction of life spent traveling outbound before bounce (hit rays). */
const OUT_END_BASE = 0.55;
/** Baseline void tip completion fraction of life. */
const VOID_OUT_BASE = 0.85;

function outEndFor(tipSpeedMul: number): number {
  const mul = Math.max(0.1, tipSpeedMul);
  return Math.min(0.92, OUT_END_BASE / mul);
}

function voidOutFor(tipSpeedMul: number): number {
  const mul = Math.max(0.1, tipSpeedMul);
  return Math.min(0.98, VOID_OUT_BASE / mul);
}

/** Optional path lengths so tip keeps constant px/s on outbound + reflect. */
export type BeamTipPathLens = {
  tipSpeedMul?: number;
  /** Outbound path length px (ox→hx). */
  outLen?: number;
  /** Reflect path length px (hx→rx) — remaining range, not hit×stub. */
  refLen?: number;
};

/**
 * Outbound / reflect progress of the traveling tip (0..1 each).
 * With `outLen`+`refLen` on a hit: tip travels both segments at the same px/s
 * over a shared travel window (`voidOutFor(tipSpeedMul)`), so long rLen uses
 * remaining life budget instead of zipping a stub then lingering.
 * Without lengths, legacy fixed outEnd + burn-remaining-on-stub is kept.
 */
export function beamTipProgress(
  age: number,
  life: number,
  hit: boolean,
  tipSpeedMulOrOpts: number | BeamTipPathLens = 1,
): {
  outP: number;
  refP: number;
} {
  const opts: BeamTipPathLens =
    typeof tipSpeedMulOrOpts === "number"
      ? { tipSpeedMul: tipSpeedMulOrOpts }
      : tipSpeedMulOrOpts;
  const tipSpeedMul = opts.tipSpeedMul ?? 1;
  const u = Math.max(0, Math.min(1, age / Math.max(1e-6, life)));
  if (!hit) {
    return { outP: Math.min(1, u / voidOutFor(tipSpeedMul)), refP: 0 };
  }
  const outLen = opts.outLen;
  const refLen = opts.refLen;
  if (outLen != null && refLen != null && outLen > 1e-6) {
    // Constant tip px/s: share travel window by path length (life not reset).
    const travelEnd = voidOutFor(tipSpeedMul);
    const total = outLen + Math.max(0, refLen);
    const outEnd = Math.max(1e-4, travelEnd * (outLen / total));
    const refSpan = Math.max(1e-4, travelEnd - outEnd);
    if (u < outEnd) {
      return { outP: u / outEnd, refP: 0 };
    }
    const uRef = u - outEnd;
    if (uRef >= refSpan) return { outP: 1, refP: 1 };
    return { outP: 1, refP: uRef / refSpan };
  }
  const outEnd = outEndFor(tipSpeedMul);
  if (u < outEnd) {
    return { outP: u / outEnd, refP: 0 };
  }
  // Legacy (no lengths): burn entire remaining life on geometry stub
  return { outP: 1, refP: (u - outEnd) / (1 - outEnd) };
}

/** Outbound + reflect path lengths for a live beam slot. */
export function beamPathLens(s: {
  ox: number;
  oy: number;
  hx: number;
  hy: number;
  rx: number;
  ry: number;
}): { outLen: number; refLen: number } {
  return {
    outLen: Math.hypot(s.hx - s.ox, s.hy - s.oy) || 1e-3,
    refLen: Math.hypot(s.rx - s.hx, s.ry - s.hy),
  };
}

/** Tip arrives at 1/3 of launch line weight (origin keeps current thickness). */
const TIP_WIDTH_MUL = 1 / 3;

/** Spatial taper: t=0 origin → t=1 arrival. */
export function beamWidthAt(t: number, launchWidth: number): number {
  const u = Math.max(0, Math.min(1, t));
  return launchWidth * (1 - u * (1 - TIP_WIDTH_MUL));
}

/**
 * Loudness → lit rail/core launch widths.
 * `amp` is lobe×event scale (quiet stride low; scrape/hold high).
 */
export function litLaunchWidths(amp: number): { rail: number; core: number } {
  const a = Math.max(0, amp);
  return {
    rail: 0.55 + a * 1.35,
    core: 1.05 + a * 1.75,
  };
}

/**
 * Tip-kiss / bounce crest scale — soft ripple, not cartoon spark×3.
 * Prior hard burst used 3; wave metaphor keeps amp coupling, lowers geometry aggression.
 */
export const CONTACT_BURST_SIZE_MUL = 1.15;

/** Fire contact stamp when tip kisses opaque face (sync with bounce, not early stub). */
export const CONTACT_OUT_P_KISS = 0.98;

/**
 * Reflect rail energy bleed (wave continuation).
 * `refP` advances crest; `ageU` = age/life fades leftover linger softly.
 */
export function reflectEnergyMul(refP: number, ageU: number): number {
  const p = Math.max(0, Math.min(1, refP));
  const u = Math.max(0, Math.min(1, ageU));
  const alongCrest = 1 - p * 0.45;
  const ageBleed = 1 - Math.max(0, u - 0.32) * 0.62;
  return Math.max(0.16, alongCrest * ageBleed);
}

/** Soft tangential crest half-width at tip-kiss (ripple front, not spark stub). */
export function waveCrestHalf(amp: number, peak = 0): number {
  return (4 + Math.max(0, amp) * 8.5 + Math.max(0, peak) * 3) * CONTACT_BURST_SIZE_MUL;
}

/** Wall-bounce contact rail thickness ∝ amp (sound size). */
export function contactRailWidth(amp: number, bright = 0): number {
  const m = CONTACT_BURST_SIZE_MUL;
  return (1.05 + Math.max(0, amp) * 1.55 + bright) * m;
}

type LitSegmentOpts = {
  exteriorN?: { nx: number; ny: number };
  /** Bounce/reflect: soft crest + energy bleed (outbound keeps readable tip). */
  softCrest?: boolean;
  /** Multiplies rail/core/tip alphas (reflectEnergyMul). */
  energyMul?: number;
};

function asLitOpts(
  exteriorOrOpts?: { nx: number; ny: number } | LitSegmentOpts,
): LitSegmentOpts {
  if (exteriorOrOpts == null) return {};
  if (
    "exteriorN" in exteriorOrOpts ||
    "softCrest" in exteriorOrOpts ||
    "energyMul" in exteriorOrOpts
  ) {
    return exteriorOrOpts as LitSegmentOpts;
  }
  const n = exteriorOrOpts as { nx: number; ny: number };
  return { exteriorN: n };
}

/**
 * Progressive blue light — only the traveled portion of the ray (no white flash).
 * Launch thickness ∝ amp; arrival tip tapers to 1/3.
 * Reflect path: `softCrest` + `energyMul` → wave crest / age bleed, not hard specular tip.
 */
function drawLitSegment(
  gfx: LineDrawTarget,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  color: number,
  amp: number,
  progress: number,
  railAlpha: number,
  exteriorOrOpts?: { nx: number; ny: number } | LitSegmentOpts,
): void {
  const opts = asLitOpts(exteriorOrOpts);
  const p = Math.max(0, Math.min(1, progress));
  if (p <= 0.001) return;
  const energy = Math.max(0, opts.energyMul ?? 1);
  const soft = opts.softCrest === true;
  const segDx = bx - ax;
  const segDy = by - ay;
  const segLen = Math.hypot(segDx, segDy) || 1;
  const idx = segDx / segLen;
  const idy = segDy / segLen;

  let ex = bx;
  let ey = by;
  if (opts.exteriorN) {
    // Geometric inset only — do not progress-cap (old 0.94 froze tip before bounce).
    const inset = 1.5 + amp * 1.2;
    ex = bx - idx * inset + opts.exteriorN.nx * 0.5;
    ey = by - idy * inset + opts.exteriorN.ny * 0.5;
  }
  const pCap = p;
  const tipX = ax + (ex - ax) * pCap;
  const tipY = ay + (ey - ay) * pCap;

  const { rail: railW0, core: coreW0 } = litLaunchWidths(amp);
  const railW = soft ? railW0 * 1.12 : railW0;
  const coreW = soft ? coreW0 * 0.82 : coreW0;
  const steps = Math.max(5, Math.min(18, Math.ceil((segLen * pCap) / 22)));
  for (let i = 0; i < steps; i++) {
    const t0 = (i / steps) * pCap;
    const t1 = ((i + 1) / steps) * pCap;
    const tm = (t0 + t1) * 0.5;
    // Soft crest: thicker near tip, fades toward origin (energy along wave front)
    const along = soft ? 0.55 + tm * 0.55 : 1;
    const x0 = ax + (ex - ax) * t0;
    const y0 = ay + (ey - ay) * t0;
    const x1 = ax + (ex - ax) * t1;
    const y1 = ay + (ey - ay) * t1;
    gfx.lineStyle(
      beamWidthAt(tm, railW),
      color,
      railAlpha * 0.4 * energy * along,
    );
    gfx.lineBetween(x0, y0, x1, y1);
  }

  const trail = Math.max(0, pCap - (soft ? 0.22 : 0.14));
  const coreSteps = Math.max(3, Math.ceil(steps * (soft ? 0.28 : 0.35)));
  for (let i = 0; i < coreSteps; i++) {
    const t0 = trail + ((pCap - trail) * i) / coreSteps;
    const t1 = trail + ((pCap - trail) * (i + 1)) / coreSteps;
    const tm = (t0 + t1) * 0.5;
    const x0 = ax + (ex - ax) * t0;
    const y0 = ay + (ey - ay) * t0;
    const x1 = ax + (ex - ax) * t1;
    const y1 = ay + (ey - ay) * t1;
    const coreA = soft
      ? (0.32 + amp * 0.12) * energy
      : (0.55 + amp * 0.2) * energy;
    gfx.lineStyle(beamWidthAt(tm, coreW), color, coreA);
    gfx.lineBetween(x0, y0, x1, y1);
  }

  if (soft) {
    // Thin soft tip — no fat fillCircle beads along the wall kiss
    const tipLen = (4 + amp * 2.5) * TIP_WIDTH_MUL;
    gfx.lineStyle(
      beamWidthAt(1, 0.55 + amp * 0.35),
      color,
      (0.35 + amp * 0.12) * energy,
    );
    gfx.lineBetween(tipX - idx * tipLen, tipY - idy * tipLen, tipX, tipY);
    return;
  }

  const tipLen = (5 + amp * 4) * TIP_WIDTH_MUL;
  gfx.lineStyle(beamWidthAt(1, 0.7 + amp * 0.55), color, 0.65 * energy);
  gfx.lineBetween(tipX - idx * tipLen, tipY - idy * tipLen, tipX, tipY);
}

function drawContactStamp(gfx: LineDrawTarget, c: ContactParticle): void {
  // Wall / loot / exit tip-kiss: no live thick stamp — scene bakes obstacle
  // perimeter into memoryPaintRt instead (avoids beaded L-corner blobs).
  if (
    c.kind === "wall" ||
    c.kind === "loot_low" ||
    c.kind === "loot_high" ||
    c.kind === "exit"
  ) {
    return;
  }
  const u = Math.max(0, c.life / c.maxLife);
  const peakBoost = c.peak * u;
  const surface = contactSurfaceColor(c.kind);
  const nl = Math.hypot(c.nx, c.ny) || 1;
  const nx = c.nx / nl;
  const ny = c.ny / nl;
  const tx = -ny;
  const ty = nx;
  const m = CONTACT_BURST_SIZE_MUL;
  const half = (5.5 + c.amp * 18) * m;
  const lift = (2.5 + c.amp * 1.2) * m;
  const cx = c.x + nx * lift;
  const cy = c.y + ny * lift;
  const ax = cx - tx * half;
  const ay = cy - ty * half;
  const bx = cx + tx * half;
  const by = cy + ty * half;

  /** Soft interior wash — pursuer/player threat only. */
  const interior = objectInteriorColor(c.kind);
  if (interior != null) {
    const ir = (9 + c.amp * 7 + peakBoost * 4) * m;
    const ix = c.x - nx * (ir * 0.4);
    const iy = c.y - ny * (ir * 0.4);
    gfx.fillStyle(interior, 0.22 + 0.4 * u * peakBoost);
    gfx.fillCircle(ix, iy, ir);
    gfx.fillStyle(interior, 0.35 + 0.45 * u * peakBoost);
    gfx.fillCircle(ix, iy, ir * 0.55);
    gfx.fillStyle(interior, 0.2 + 0.3 * u * peakBoost);
    gfx.fillCircle(c.x - nx * (ir * 0.15), c.y - ny * (ir * 0.15), ir * 0.9);
  }

  gfx.lineStyle(contactRailWidth(c.amp, 0), surface, 0.14 + 0.24 * u * peakBoost);
  gfx.lineBetween(ax, ay, bx, by);
  gfx.lineStyle(
    (0.85 + c.amp * 0.85 + peakBoost * 0.4) * m,
    surface,
    0.22 + 0.35 * u * peakBoost,
  );
  const tipHalf = (2.2 + c.amp * 4.2) * m;
  gfx.lineBetween(
    cx - tx * tipHalf,
    cy - ty * tipHalf,
    cx + tx * tipHalf,
    cy + ty * tipHalf,
  );
  gfx.lineStyle(
    (0.7 + c.amp * 0.55) * m,
    surface,
    0.16 + 0.28 * u * peakBoost,
  );
  const spike = (5 + c.amp * 4) * m;
  gfx.lineBetween(cx, cy, cx + nx * spike, cy + ny * spike);
}

/** Fog erase streak — tapered strip, no circular tips. */
export function eraseRayStreak(
  gfx: LineDrawTarget,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  halfW: number,
): void {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.hypot(dx, dy) || 1;
  const idx = dx / len;
  const idy = dy / len;
  const px = -idy;
  const py = idx;
  const mid = 0.82;
  const mx = ax + dx * mid;
  const my = ay + dy * mid;
  const hw0 = halfW;
  const hw1 = halfW * 0.55;
  gfx.fillStyle(0xffffff, 1);
  gfx.fillTriangle(
    ax + px * hw0,
    ay + py * hw0,
    ax - px * hw0,
    ay - py * hw0,
    mx + px * hw1,
    my + py * hw1,
  );
  gfx.fillTriangle(
    ax - px * hw0,
    ay - py * hw0,
    mx - px * hw1,
    my - py * hw1,
    mx + px * hw1,
    my + py * hw1,
  );
  gfx.fillTriangle(bx, by, mx + px * hw1, my + py * hw1, mx - px * hw1, my - py * hw1);
}

function lerpSeg(
  ax: number,
  ay: number,
  bx: number,
  by: number,
  t0: number,
  t1: number,
): { x0: number; y0: number; x1: number; y1: number } {
  const a = Math.max(0, Math.min(1, t0));
  const b = Math.max(0, Math.min(1, t1));
  return {
    x0: ax + (bx - ax) * a,
    y0: ay + (by - ay) * a,
    x1: ax + (bx - ax) * b,
    y1: ay + (by - ay) * b,
  };
}

export class SonarLineParticlePool {
  readonly beams: LineParticle[];
  readonly contacts: ContactParticle[];
  private readonly freeBeams: number[] = [];
  private readonly freeContacts: number[] = [];
  /** Scratch: alive beam indices sorted by stamp (oldest → newest). */
  private readonly drawBeamOrder: number[] = [];
  /** Scratch: alive contact indices sorted by stamp (oldest → newest). */
  private readonly drawContactOrder: number[] = [];
  private stamp = 0;
  private _aliveBeams = 0;
  private _aliveContacts = 0;
  /** Tip travel only; does not affect life burn (sonarAgeMul). */
  tipSpeedMul: number;

  constructor(opts: SonarLinePoolOptions) {
    const bc = Math.max(1, Math.floor(opts.beamCapacity));
    const cc = Math.max(1, Math.floor(opts.contactCapacity));
    this.tipSpeedMul = Math.max(0.1, opts.tipSpeedMul ?? 1);
    this.beams = Array.from({ length: bc }, () => makeBeamSlot());
    this.contacts = Array.from({ length: cc }, () => makeContactSlot());
    for (let i = bc - 1; i >= 0; i--) this.freeBeams.push(i);
    for (let i = cc - 1; i >= 0; i--) this.freeContacts.push(i);
  }

  get aliveBeamCount(): number {
    return this._aliveBeams;
  }

  get aliveContactCount(): number {
    return this._aliveContacts;
  }

  get beamCapacity(): number {
    return this.beams.length;
  }

  clear(): void {
    for (let i = 0; i < this.beams.length; i++) {
      const s = this.beams[i]!;
      if (s.alive) {
        s.alive = false;
        this.freeBeams.push(i);
      }
    }
    for (let i = 0; i < this.contacts.length; i++) {
      const s = this.contacts[i]!;
      if (s.alive) {
        s.alive = false;
        this.freeContacts.push(i);
      }
    }
    this._aliveBeams = 0;
    this._aliveContacts = 0;
  }

  /**
   * Emit one line particle from a cast beam (particle-like).
   * `preferSpent` never kills mid-flight tips (used while moving).
   * Returns null when the pool is full of still-traveling beams.
   */
  emitBeam(
    beam: SonarBeam,
    life: number,
    mode: BeamAcquireMode = "overwriteOldest",
    railColor?: number,
  ): LineParticle | null {
    const idx = this.acquireBeamIndex(mode);
    if (idx < 0) return null;
    const s = this.beams[idx]!;
    s.alive = true;
    s.ox = beam.ox;
    s.oy = beam.oy;
    s.hx = beam.hx;
    s.hy = beam.hy;
    s.rx = beam.rx;
    s.ry = beam.ry;
    s.nx = beam.nx;
    s.ny = beam.ny;
    s.amp = beam.amp;
    s.kind = beam.kind;
    s.hit = beam.hit;
    s.color = railColor ?? beamColor(beam.kind);
    s.age = 0;
    s.life = Math.max(0.05, life);
    s.contacted = false;
    s.memOut = 0;
    s.memRef = 0;
    s.stamp = ++this.stamp;
    return s;
  }

  emitBeams(
    beams: readonly SonarBeam[],
    life: number,
    mode: BeamAcquireMode = "overwriteOldest",
    railColor?: number,
  ): void {
    for (let i = 0; i < beams.length; i++) {
      this.emitBeam(beams[i]!, life, mode, railColor);
    }
  }

  /**
   * Age particles; kill expired; fire contact once when tip kisses an opaque face.
   * Returns sawExit / sawPursuer flags for HUD cues.
   */
  update(
    dt: number,
    ageMul: number,
    onContact?: (p: LineParticle, peak: number) => void,
  ): { sawExit: boolean; sawPursuer: boolean } {
    let sawExit = false;
    let sawPursuer = false;
    const step = dt * ageMul;
    for (let i = 0; i < this.beams.length; i++) {
      const s = this.beams[i]!;
      if (!s.alive) continue;
      s.age += step;
      if (s.age >= s.life) {
        this.killBeam(i);
        continue;
      }
      if (s.kind === "exit") sawExit = true;
      if (s.kind === "pursuer") sawPursuer = true;

      if (!s.hit) {
        s.contacted = true;
        continue;
      }
      if (s.contacted || !onContact) continue;
      const { outP } = this.tipProgressFor(s);
      // Burst at tip-kiss / bounce — not mid-approach, and no post-freeze wait.
      if (outP >= CONTACT_OUT_P_KISS) {
        s.contacted = true;
        onContact(s, 1);
      }
    }

    for (let i = 0; i < this.contacts.length; i++) {
      const c = this.contacts[i]!;
      if (!c.alive) continue;
      c.life -= step;
      c.peak = Math.max(0, c.peak - 1.8 * step);
      if (c.life <= 0) this.killContact(i);
    }

    return { sawExit, sawPursuer };
  }

  /** Merge-or-spawn contact stamp at opaque hit (particle pool). */
  emitContact(from: LineParticle, peak: number, maxLife: number): void {
    if (!from.hit || from.kind === "void") return;
    for (let i = 0; i < this.contacts.length; i++) {
      const c = this.contacts[i]!;
      if (!c.alive || c.kind !== from.kind) continue;
      if (Math.hypot(c.x - from.hx, c.y - from.hy) < 14) {
        c.life = Math.max(c.life, c.maxLife * 0.85);
        c.peak = Math.max(c.peak, peak);
        c.amp = Math.max(c.amp, from.amp);
        c.nx = from.nx;
        c.ny = from.ny;
        return;
      }
    }
    const idx = this.acquireContactIndex();
    const c = this.contacts[idx]!;
    c.alive = true;
    c.x = from.hx;
    c.y = from.hy;
    c.nx = from.nx;
    c.ny = from.ny;
    c.kind = from.kind;
    c.amp = from.amp;
    c.maxLife = Math.max(0.05, maxLife);
    c.life = c.maxLife;
    c.peak = peak;
    c.stamp = ++this.stamp;
  }

  draw(gfx: LineDrawTarget): void {
    gfx.clear();
    // Slot index ≠ spawn order (free-list LIFO / preferSpent recycle). Newer
    // PlayerSonar (e.g. orange scrape) must paint after older blue or it reads under.
    this.drawBeamOrder.length = 0;
    for (let i = 0; i < this.beams.length; i++) {
      if (this.beams[i]!.alive) this.drawBeamOrder.push(i);
    }
    this.drawBeamOrder.sort(
      (a, b) => this.beams[a]!.stamp - this.beams[b]!.stamp,
    );
    for (let k = 0; k < this.drawBeamOrder.length; k++) {
      this.drawBeam(gfx, this.beams[this.drawBeamOrder[k]!]!);
    }
    this.drawContactOrder.length = 0;
    for (let i = 0; i < this.contacts.length; i++) {
      if (this.contacts[i]!.alive) this.drawContactOrder.push(i);
    }
    this.drawContactOrder.sort(
      (a, b) => this.contacts[a]!.stamp - this.contacts[b]!.stamp,
    );
    for (let k = 0; k < this.drawContactOrder.length; k++) {
      drawContactStamp(gfx, this.contacts[this.drawContactOrder[k]!]!);
    }
  }

  /**
   * Progressive fog punch — only up to the traveling tip (grows with the line).
   */
  eraseFog(gfx: LineDrawTarget): void {
    for (let i = 0; i < this.beams.length; i++) {
      const s = this.beams[i]!;
      if (!s.alive) continue;
      const { outP, refP } = this.tipProgressFor(s);
      const hw = 3.2 + s.amp * 3.5;
      if (outP > 0.01) {
        const tip = lerpSeg(s.ox, s.oy, s.hx, s.hy, 0, outP);
        eraseRayStreak(gfx, tip.x0, tip.y0, tip.x1, tip.y1, hw);
      }
      if (s.hit && refP > 0.01) {
        const nl = Math.hypot(s.nx, s.ny) || 1;
        const sx = s.hx + (s.nx / nl) * 3;
        const sy = s.hy + (s.ny / nl) * 3;
        const tip = lerpSeg(sx, sy, s.rx, s.ry, 0, refP);
        eraseRayStreak(gfx, tip.x0, tip.y0, tip.x1, tip.y1, hw * 0.85);
      }
    }
  }

  /**
   * Stamp newly traveled delta into fog-of-war memory + faction-colored afterimage.
   * Each beam paints with its own rail color (orange scrape, blue stride, red pursuer,
   * loot-tinted reflect) — not a forced blue-only memory tint.
   */
  stampMemoryProgressive(
    eraseGfx: LineDrawTarget,
    paintGfx: LineDrawTarget | null,
    halfW: number,
  ): void {
    for (let i = 0; i < this.beams.length; i++) {
      const s = this.beams[i]!;
      if (!s.alive) continue;
      const { outP, refP } = this.tipProgressFor(s);
      if (outP > s.memOut + 0.002) {
        const seg = lerpSeg(s.ox, s.oy, s.hx, s.hy, s.memOut, outP);
        eraseRayStreak(
          eraseGfx,
          seg.x0,
          seg.y0,
          seg.x1,
          seg.y1,
          halfW * (0.55 + s.amp * 0.35),
        );
        if (paintGfx) {
          const { hard, soft } = memoryPaintFromRail(s.color);
          paintGfx.lineStyle(4.5 + s.amp * 1.6, soft, 0.32);
          paintGfx.lineBetween(seg.x0, seg.y0, seg.x1, seg.y1);
          paintGfx.lineStyle(1.6 + s.amp * 0.5, hard, 0.5);
          paintGfx.lineBetween(seg.x0, seg.y0, seg.x1, seg.y1);
        }
        s.memOut = outP;
      }
      if (s.hit && refP > s.memRef + 0.002) {
        const nl = Math.hypot(s.nx, s.ny) || 1;
        const sx = s.hx + (s.nx / nl) * 2;
        const sy = s.hy + (s.ny / nl) * 2;
        const seg = lerpSeg(sx, sy, s.rx, s.ry, s.memRef, refP);
        eraseRayStreak(eraseGfx, seg.x0, seg.y0, seg.x1, seg.y1, halfW * 0.5);
        if (paintGfx) {
          const bounce = reflectRailColor(s.kind, s.color);
          const { hard, soft } = memoryPaintFromRail(bounce);
          // Soft ripple afterimage — lower alpha than outbound (energy bleed)
          paintGfx.lineStyle(3.2 + s.amp * 0.85, soft, 0.18);
          paintGfx.lineBetween(seg.x0, seg.y0, seg.x1, seg.y1);
          paintGfx.lineStyle(1.1 + s.amp * 0.35, hard, 0.28);
          paintGfx.lineBetween(seg.x0, seg.y0, seg.x1, seg.y1);
        }
        s.memRef = refP;
      }
    }
  }

  private drawBeam(gfx: LineDrawTarget, s: LineParticle): void {
    const color = s.color;
    const bounceColor = reflectRailColor(s.kind, color);
    const { outP, refP } = this.tipProgressFor(s);
    const ageU = s.age / Math.max(1e-6, s.life);
    if (!s.hit) {
      drawLitSegment(
        gfx,
        s.ox,
        s.oy,
        s.hx,
        s.hy,
        color,
        s.amp * 0.85,
        outP,
        0.45 + (1 - outP) * 0.2,
      );
      return;
    }
    if (refP <= 0) {
      drawLitSegment(
        gfx,
        s.ox,
        s.oy,
        s.hx,
        s.hy,
        color,
        s.amp,
        outP,
        0.5 + (1 - outP) * 0.3,
        { exteriorN: { nx: s.nx, ny: s.ny } },
      );
      return;
    }
    // Soft leftover outbound after kiss — fades with crest travel (not hard stub)
    gfx.lineStyle(
      0.5 + s.amp * 0.55,
      color,
      0.1 * (1 - refP) * reflectEnergyMul(0, ageU),
    );
    const dist = Math.hypot(s.hx - s.ox, s.hy - s.oy) || 1;
    const pull = 2 / dist;
    gfx.lineBetween(
      s.ox,
      s.oy,
      s.hx - (s.hx - s.ox) * pull + s.nx * 0.5,
      s.hy - (s.hy - s.oy) * pull + s.ny * 0.5,
    );
    const nl = Math.hypot(s.nx, s.ny) || 1;
    const sx = s.hx + (s.nx / nl) * 3;
    const sy = s.hy + (s.ny / nl) * 3;
    // Bounce = ripple continuation: soft crest + energy bleed (keep outbound readable)
    const energy = reflectEnergyMul(refP, ageU);
    const bounceAmp = s.kind === "wall" ? s.amp * 0.95 : s.amp * 1.0;
    const bounceAlpha = (s.kind === "wall" ? 0.52 : 0.48) * energy;
    drawLitSegment(
      gfx,
      sx,
      sy,
      s.rx,
      s.ry,
      bounceColor,
      bounceAmp,
      refP,
      bounceAlpha,
      { softCrest: true, energyMul: energy },
    );
  }

  private tipProgressFor(s: LineParticle): { outP: number; refP: number } {
    const { outLen, refLen } = beamPathLens(s);
    return beamTipProgress(s.age, s.life, s.hit, {
      tipSpeedMul: this.tipSpeedMul,
      outLen,
      refLen,
    });
  }

  private beamOutboundDone(s: LineParticle): boolean {
    const { outP, refP } = this.tipProgressFor(s);
    if (!s.hit) return outP >= 1;
    // PreferSpent must not cut long post-bounce travel mid-path
    return outP >= 1 && refP >= 1;
  }

  /**
   * Free slot first.
   * `preferSpent`: recycle only beams whose tip finished travel (never cut mid-flight).
   * Returns -1 when preferSpent and every alive beam is still traveling.
   */
  private acquireBeamIndex(mode: BeamAcquireMode): number {
    const free = this.freeBeams.pop();
    if (free !== undefined) {
      this._aliveBeams++;
      return free;
    }
    if (mode === "preferSpent") {
      let spent = -1;
      let spentStamp = Number.POSITIVE_INFINITY;
      for (let i = 0; i < this.beams.length; i++) {
        const s = this.beams[i]!;
        if (!s.alive || !this.beamOutboundDone(s)) continue;
        if (s.stamp < spentStamp) {
          spentStamp = s.stamp;
          spent = i;
        }
      }
      return spent;
    }
    // Full — recycle oldest alive (particle emitter behavior)
    let oldest = 0;
    let oldestStamp = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.beams.length; i++) {
      const s = this.beams[i]!;
      if (s.alive && s.stamp < oldestStamp) {
        oldestStamp = s.stamp;
        oldest = i;
      }
    }
    return oldest;
  }

  private acquireContactIndex(): number {
    const free = this.freeContacts.pop();
    if (free !== undefined) {
      this._aliveContacts++;
      return free;
    }
    let oldest = 0;
    let oldestStamp = Number.POSITIVE_INFINITY;
    for (let i = 0; i < this.contacts.length; i++) {
      const s = this.contacts[i]!;
      if (s.alive && s.stamp < oldestStamp) {
        oldestStamp = s.stamp;
        oldest = i;
      }
    }
    return oldest;
  }

  private killBeam(i: number): void {
    const s = this.beams[i]!;
    if (!s.alive) return;
    s.alive = false;
    this._aliveBeams = Math.max(0, this._aliveBeams - 1);
    this.freeBeams.push(i);
  }

  private killContact(i: number): void {
    const s = this.contacts[i]!;
    if (!s.alive) return;
    s.alive = false;
    this._aliveContacts = Math.max(0, this._aliveContacts - 1);
    this.freeContacts.push(i);
  }
}
